const GUEST_CART_KEY = "miniMilesGuestCart";
const GUEST_CHECKOUT_SNAPSHOTS_KEY = "miniMilesGuestCheckoutSnapshots";

const normalize = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const validCheckoutSessionId = (value) =>
  /^[a-f\d]{24}$/i.test(String(value || ""));

const validQuantity = (value) =>
  Number.isSafeInteger(Number(value)) &&
  Number(value) >= 1 &&
  Number(value) <= 100;

const getGuestCheckoutSnapshots = () => {
  try {
    const stored = localStorage.getItem(GUEST_CHECKOUT_SNAPSHOTS_KEY);
    const snapshots = stored ? JSON.parse(stored) : {};

    return snapshots &&
      typeof snapshots === "object" &&
      !Array.isArray(snapshots)
      ? snapshots
      : {};
  } catch {
    return {};
  }
};

const saveGuestCheckoutSnapshots = (snapshots) => {
  localStorage.setItem(GUEST_CHECKOUT_SNAPSHOTS_KEY, JSON.stringify(snapshots));
};

const matchesGuestItem = (item, incoming) => {
  if (String(item.productId) !== String(incoming.productId)) {
    return false;
  }

  if (incoming.variantId || item.variantId) {
    return (
      Boolean(incoming.variantId) &&
      Boolean(item.variantId) &&
      String(item.variantId) === String(incoming.variantId)
    );
  }

  return (
    normalize(item.size) === normalize(incoming.size) &&
    normalize(item.color) === normalize(incoming.color) &&
    normalize(item.volume) === normalize(incoming.volume)
  );
};

export const getGuestCart = () => {
  try {
    const stored = localStorage.getItem(GUEST_CART_KEY);
    const items = stored ? JSON.parse(stored) : [];

    return Array.isArray(items) ? items : [];
  } catch {
    return [];
  }
};

export const saveGuestCart = (items) => {
  if (!Array.isArray(items)) {
    throw new Error("Guest cart must be an array.");
  }

  localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items));

  window.dispatchEvent(new Event("miniMilesGuestCartUpdated"));
};

export const clearGuestCart = () => {
  localStorage.removeItem(GUEST_CART_KEY);

  window.dispatchEvent(new Event("miniMilesGuestCartUpdated"));
};

export const addGuestCartItem = (incoming) => {
  const items = getGuestCart();
  const quantity = Number(incoming.quantity ?? 1);

  if (!validQuantity(quantity)) {
    throw new Error("Please select a quantity between 1 and 100.");
  }

  if (!incoming.productId) {
    throw new Error("A product ID is required.");
  }

  if (incoming.fulfillmentMethod === "printify" && !incoming.variantId) {
    throw new Error("Please select a valid product variation.");
  }

  const existing = items.find((item) => matchesGuestItem(item, incoming));

  if (existing) {
    const newQuantity = Number(existing.quantity) + quantity;

    if (!validQuantity(newQuantity)) {
      throw new Error("Maximum quantity is 100.");
    }

    existing.quantity = newQuantity;
  } else {
    items.push({
      productId: String(incoming.productId),
      variantId: incoming.variantId ? String(incoming.variantId) : null,
      externalVariantId: incoming.externalVariantId || "",
      selectedOptions: incoming.selectedOptions || {},
      fulfillmentMethod: incoming.fulfillmentMethod || "self",
      name: incoming.name || "",
      price: Number(incoming.price || 0),
      image: incoming.image || "",
      size: incoming.size || "",
      color: incoming.color || "",
      volume: incoming.volume || "",
      quantity,
    });
  }

  saveGuestCart(items);

  return items;
};

export const updateGuestCartQuantity = (target, quantity) => {
  const items = getGuestCart();
  const newQuantity = Number(quantity);

  if (!validQuantity(newQuantity)) {
    throw new Error("Quantity must be between 1 and 100.");
  }

  const item = items.find((existing) => matchesGuestItem(existing, target));

  if (!item) {
    throw new Error("Cart item not found.");
  }

  item.quantity = newQuantity;

  saveGuestCart(items);

  return items;
};

export const removeGuestCartItem = (target) => {
  const items = getGuestCart();

  const updated = items.filter((item) => !matchesGuestItem(item, target));

  saveGuestCart(updated);

  return updated;
};

/**
 * Save a snapshot of the exact guest-cart items submitted
 * for a checkout session.
 *
 * Call this only after the backend has returned a valid
 * checkoutSessionId, using the cartItems from the actual
 * createPaymentIntent request.
 *
 * Do not save a new snapshot for an already recorded
 * checkout session.
 */
export const saveGuestCheckoutSnapshot = (
  checkoutSessionId,
  submittedItems
) => {
  if (!validCheckoutSessionId(checkoutSessionId)) {
    throw new Error("Invalid checkout session reference.");
  }

  if (!Array.isArray(submittedItems) || submittedItems.length === 0) {
    throw new Error("Cannot snapshot an empty checkout cart.");
  }

  const snapshots = getGuestCheckoutSnapshots();

  if (snapshots[checkoutSessionId]) {
    return snapshots[checkoutSessionId];
  }

  const items = submittedItems.map((item) => {
    const quantity = Number(item.quantity);

    if (!item.productId || !validQuantity(quantity)) {
      throw new Error("Invalid checkout snapshot item.");
    }

    return {
      productId: String(item.productId),
      variantId: item.variantId ? String(item.variantId) : null,
      size: item.size || "",
      color: item.color || "",
      volume: item.volume || "",
      quantity,
    };
  });

  const snapshot = {
    checkoutSessionId,
    items,
    createdAt: new Date().toISOString(),
    cleaned: false,
  };

  snapshots[checkoutSessionId] = snapshot;

  saveGuestCheckoutSnapshots(snapshots);

  return snapshot;
};

/**
 * Remove purchased guest-cart items after the server
 * confirms that the checkout has a paid order.
 *
 * Safety rules:
 * - Requires a saved checkout snapshot.
 * - Requires a confirmed, paid order.
 * - Verifies that the order's purchased items match
 *   the checkout snapshot.
 * - Removes only lines whose quantities have not
 *   changed since the snapshot.
 * - Never clears unrelated cart items.
 * - Marks the checkout as cleaned so repeated page
 *   visits do not subtract the same purchase again.
 *
 * If a purchased line changed after checkout began,
 * it is deliberately left in the cart.
 */
export const cleanupConfirmedGuestCheckout = ({ checkoutSessionId, order }) => {
  if (!validCheckoutSessionId(checkoutSessionId)) {
    return {
      cleaned: false,
      reason: "invalid_checkout_session",
    };
  }

  if (
    !order ||
    order.paymentStatus !== "paid" ||
    !Array.isArray(order.items) ||
    order.items.length === 0
  ) {
    return {
      cleaned: false,
      reason: "order_not_confirmed",
    };
  }

  const snapshots = getGuestCheckoutSnapshots();
  const snapshot = snapshots[checkoutSessionId];

  if (!snapshot) {
    return {
      cleaned: false,
      reason: "snapshot_missing",
    };
  }

  if (snapshot.cleaned === true) {
    return {
      cleaned: false,
      reason: "already_cleaned",
    };
  }

  if (
    !Array.isArray(snapshot.items) ||
    snapshot.items.length !== order.items.length
  ) {
    return {
      cleaned: false,
      reason: "snapshot_order_mismatch",
    };
  }

  const orderItemsMatchSnapshot = snapshot.items.every((snapshotItem) => {
    const matchingOrderItems = order.items.filter((orderItem) =>
      matchesGuestItem(snapshotItem, orderItem)
    );

    return (
      matchingOrderItems.length === 1 &&
      Number(matchingOrderItems[0].quantity) === Number(snapshotItem.quantity)
    );
  });

  if (!orderItemsMatchSnapshot) {
    return {
      cleaned: false,
      reason: "snapshot_order_mismatch",
    };
  }

  const currentCart = getGuestCart();

  let removedItems = 0;

  const updatedCart = currentCart.filter((currentItem) => {
    const purchasedItem = snapshot.items.find((snapshotItem) =>
      matchesGuestItem(currentItem, snapshotItem)
    );

    if (!purchasedItem) {
      return true;
    }

    // If the customer changed this line while payment
    // was processing, preserve it rather than deleting
    // potentially newer additions.
    if (Number(currentItem.quantity) !== Number(purchasedItem.quantity)) {
      return true;
    }

    removedItems += 1;

    return false;
  });

  // Mark the checkout as processed before dispatching
  // the cart-update event. This prevents repeated cleanup
  // when the confirmation page is revisited.
  snapshots[checkoutSessionId] = {
    ...snapshot,
    cleaned: true,
    cleanedAt: new Date().toISOString(),
  };

  saveGuestCheckoutSnapshots(snapshots);

  if (removedItems > 0) {
    saveGuestCart(updatedCart);
  }

  return {
    cleaned: true,
    removedItems,
    remainingItems: updatedCart.length,
  };
};
